import type { WorkflowAuditEntryResponse } from '@/lib/api';

export type WorkflowAuditTone = 'destructive' | 'warning' | 'neutral';
export type WorkflowAuditEventFilter = 'all' | WorkflowAuditEntryResponse['event_type'];
export type WorkflowAuditDateRange = 'today' | '7d' | '30d' | 'all';
export type WorkflowAuditPresetId = 'denials_by_cohort' | 'conversation_audit' | 'high_pressure';

export type TemplateGovernanceEventType = 'template_rollout_denied' | 'template_override_denied';

const TEMPLATE_GOVERNANCE_EVENT_TYPES = new Set<TemplateGovernanceEventType>([
  'template_rollout_denied',
  'template_override_denied',
]);

export interface WorkflowAuditFilterOption {
  value: WorkflowAuditEventFilter;
  label: string;
}

export const WORKFLOW_AUDIT_EVENT_FILTER_OPTIONS: WorkflowAuditFilterOption[] = [
  { value: 'all', label: 'All events' },
  { value: 'admission_denied', label: 'Admission denied' },
  { value: 'cancel', label: 'Cancelled' },
  { value: 'abandon', label: 'Abandoned' },
  { value: 'approve', label: 'Approved' },
  { value: 'reject', label: 'Rejected' },
  { value: 'delete_tombstone', label: 'Workflow deleted' },
  { value: 'template_rollout_denied', label: 'Rollout denied' },
  { value: 'template_override_denied', label: 'Override denied' },
  { value: 'workflow_action', label: 'Workflow actions' },
];

export interface WorkflowAuditPresetDefinition {
  id: WorkflowAuditPresetId;
  label: string;
  description: string;
}

export const WORKFLOW_AUDIT_PRESETS: WorkflowAuditPresetDefinition[] = [
  {
    id: 'denials_by_cohort',
    label: 'Denials by cohort',
    description: 'Admission denials grouped by codebase cohort prefix.',
  },
  {
    id: 'conversation_audit',
    label: 'Conversation audit',
    description: 'Focus the feed on a single conversation trail.',
  },
  {
    id: 'high_pressure',
    label: 'High pressure',
    description: 'Recent denials that usually indicate saturation.',
  },
];

export interface WorkflowAuditPresetState {
  eventType?: WorkflowAuditEventFilter;
  actor?: string | null;
  workflowName?: string | null;
  conversationId?: string | null;
  codebaseNamePrefix?: string | null;
  dateRange?: WorkflowAuditDateRange;
}

export interface WorkflowAuditPresetContext {
  codebaseName?: string | null;
  codebaseNamePrefix?: string | null;
  conversationId?: string | null;
  dateRange?: WorkflowAuditDateRange;
}

export interface WorkflowAuditGovernanceContext {
  templateId: string | null;
  templateName: string | null;
  templateOwner: string | null;
  templateRolloutState: string | null;
  allowCodebaseOverride: boolean | null;
  operator: string | null;
  operatorRole: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  violationType: string | null;
  overrideState: string | null;
}

export interface WorkflowAuditGovernanceSummary {
  total: number;
  violations: number;
  overrideBlocked: number;
  rolloutBlocked: number;
  ownershipViolations: number;
  draftTemplates: number;
  activeTemplates: number;
  archivedTemplates: number;
  lockedOverrides: number;
  allowedOverrides: number;
  topOwner: string | null;
  topOwnerCount: number;
  topOperator: string | null;
  topOperatorCount: number;
}

export interface WorkflowAuditSavedPreset {
  id: string;
  name: string;
  state: WorkflowAuditPresetState;
  createdAt: string;
}

export function getWorkflowAuditPreset(
  state: WorkflowAuditPresetState
): WorkflowAuditPresetId | 'custom' {
  const normalizedActor = getString(state.actor);
  const normalizedWorkflowName = getString(state.workflowName);
  const normalizedConversationId = getString(state.conversationId);
  const normalizedPrefix = getString(state.codebaseNamePrefix);
  const normalizedDateRange = state.dateRange ?? 'all';

  if (
    state.eventType === 'admission_denied' &&
    normalizedDateRange === '30d' &&
    !normalizedActor &&
    !normalizedWorkflowName &&
    !normalizedConversationId &&
    normalizedPrefix
  ) {
    return 'denials_by_cohort';
  }

  if (
    state.eventType === 'all' &&
    normalizedDateRange === 'all' &&
    !normalizedActor &&
    !normalizedWorkflowName &&
    normalizedConversationId &&
    !normalizedPrefix
  ) {
    return 'conversation_audit';
  }

  if (
    state.eventType === 'admission_denied' &&
    normalizedDateRange === '7d' &&
    !normalizedActor &&
    !normalizedWorkflowName &&
    !normalizedConversationId &&
    !normalizedPrefix
  ) {
    return 'high_pressure';
  }

  return 'custom';
}

export function applyWorkflowAuditPreset(
  presetId: WorkflowAuditPresetId,
  context: WorkflowAuditPresetContext = {}
): WorkflowAuditPresetState {
  switch (presetId) {
    case 'denials_by_cohort':
      return {
        eventType: 'admission_denied',
        actor: null,
        workflowName: null,
        conversationId: null,
        codebaseNamePrefix: getString(context.codebaseNamePrefix ?? context.codebaseName) ?? null,
        dateRange: '30d',
      };
    case 'conversation_audit':
      return {
        eventType: 'all',
        actor: null,
        workflowName: null,
        conversationId: getString(context.conversationId) ?? null,
        codebaseNamePrefix: null,
        dateRange: context.dateRange ?? 'all',
      };
    case 'high_pressure':
      return {
        eventType: 'admission_denied',
        actor: null,
        workflowName: null,
        conversationId: null,
        codebaseNamePrefix: null,
        dateRange: '7d',
      };
  }
}

export function applySavedWorkflowAuditPreset(
  preset: WorkflowAuditSavedPreset
): WorkflowAuditPresetState {
  return {
    eventType: preset.state.eventType ?? 'all',
    actor: preset.state.actor ?? null,
    workflowName: preset.state.workflowName ?? null,
    conversationId: preset.state.conversationId ?? null,
    codebaseNamePrefix: preset.state.codebaseNamePrefix ?? null,
    dateRange: preset.state.dateRange ?? 'all',
  };
}

export function matchSavedWorkflowAuditPreset(
  presets: WorkflowAuditSavedPreset[],
  state: WorkflowAuditPresetState
): string | null {
  const normalizedState = applySavedWorkflowAuditPreset({
    id: 'current',
    name: 'Current',
    createdAt: '',
    state,
  });

  for (const preset of presets) {
    const candidate = applySavedWorkflowAuditPreset(preset);
    if (
      (candidate.eventType ?? 'all') === (normalizedState.eventType ?? 'all') &&
      (candidate.actor ?? null) === (normalizedState.actor ?? null) &&
      (candidate.workflowName ?? null) === (normalizedState.workflowName ?? null) &&
      (candidate.conversationId ?? null) === (normalizedState.conversationId ?? null) &&
      (candidate.codebaseNamePrefix ?? null) === (normalizedState.codebaseNamePrefix ?? null) &&
      (candidate.dateRange ?? 'all') === (normalizedState.dateRange ?? 'all')
    ) {
      return preset.id;
    }
  }

  return null;
}

export interface WorkflowActionSSEEvent {
  type: 'workflow_action';
  timestamp: number;
  runId?: string;
  workflow_run_id?: string;
  workflowName?: string;
  workflow_name?: string;
  codebaseId?: string | null;
  codebase_id?: string | null;
  codebaseName?: string | null;
  codebase_name?: string | null;
  action?: string;
  stepName?: string;
  step_name?: string;
  reason?: string;
  comment?: string;
  data?: Record<string, unknown>;
  id?: string;
}

function prettifyAuditReason(reason: string | null): string | null {
  const normalized = getString(reason);
  if (!normalized) return null;
  if (!normalized.includes('_')) return normalized;
  const words = normalized.split('_').filter(Boolean);
  if (words.length === 0) return null;
  if (normalized.startsWith('workflow_')) {
    return `Workflow ${words.slice(1).join(' ')}`;
  }
  return `${words[0][0].toUpperCase()}${words[0].slice(1)} ${words.slice(1).join(' ')}`.trim();
}

export interface WorkflowAuditFilters {
  codebaseId?: string | null;
  codebaseNamePrefix?: string;
  workflowName?: string;
  conversationId?: string;
  actor?: string;
  search?: string;
  after?: string;
  before?: string;
  eventType?: WorkflowAuditEventFilter;
}

export interface WorkflowAuditSummary {
  total: number;
  admissionDenied: number;
  workflowActions: number;
  cancelled: number;
  abandoned: number;
  approved: number;
  rejected: number;
  deleted: number;
}

export interface WorkflowAuditPolicySourceSummary {
  total: number;
  sources: Record<string, number>;
  topSource: string | null;
  topSourceCount: number;
}

export interface WorkflowTrendSample {
  timestamp: string | number;
  numerator: number;
  denominator: number;
}

export interface WorkflowTrendBucket {
  label: string;
  start: string;
  end: string;
  numerator: number;
  denominator: number;
  ratio: number;
}

export interface WorkflowTrendBucketOptions {
  bucketCount?: number;
  after?: string;
  before?: string;
}

function getString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function getBoolean(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (value === 'true') return true;
    if (value === 'false') return false;
  }
  return null;
}

function getWorkflowAuditDataRecord(entry: WorkflowAuditEntryResponse): Record<string, unknown> {
  return entry.data;
}

function getWorkflowAuditGovernanceContextFromData(
  data: Record<string, unknown>
): WorkflowAuditGovernanceContext {
  const templateId = getString(data.template_id ?? data.templateId);
  const templateName = getString(data.template_name ?? data.templateName);
  const templateOwner =
    getString(data.template_owner ?? data.templateOwner) ??
    getString(data.owner ?? data.template_owner_name);
  const templateRolloutState = getString(
    data.template_rollout_state ?? data.rollout_state ?? data.rolloutState
  );
  const allowCodebaseOverride = getBoolean(
    data.allow_codebase_override ?? data.allowCodebaseOverride
  );
  const operator =
    getString(data.operator) ??
    getString(data.operator_name ?? data.operatorName) ??
    getString(data.actor) ??
    getString(data.created_by ?? data.createdBy) ??
    getString(data.updated_by ?? data.updatedBy);
  const operatorRole = getString(data.operator_role ?? data.operatorRole ?? data.role);
  const createdBy = getString(data.created_by ?? data.createdBy);
  const updatedBy = getString(data.updated_by ?? data.updatedBy);
  const violationType = getString(
    data.violation_type ?? data.violationType ?? data.governance_violation_type
  );
  const overrideState =
    getString(data.override_state ?? data.overrideState) ??
    (allowCodebaseOverride === null ? null : allowCodebaseOverride ? 'allowed' : 'locked');

  return {
    templateId,
    templateName,
    templateOwner,
    templateRolloutState,
    allowCodebaseOverride,
    operator,
    operatorRole,
    createdBy,
    updatedBy,
    violationType,
    overrideState,
  };
}

export function getWorkflowAuditGovernanceContext(
  entry: WorkflowAuditEntryResponse
): WorkflowAuditGovernanceContext {
  const context = getWorkflowAuditGovernanceContextFromData(getWorkflowAuditDataRecord(entry));
  return {
    ...context,
    operator: context.operator ?? getString(entry.actor),
  };
}

export function isTemplateGovernanceEventType(
  eventType: WorkflowAuditEntryResponse['event_type']
): eventType is TemplateGovernanceEventType {
  return TEMPLATE_GOVERNANCE_EVENT_TYPES.has(eventType as TemplateGovernanceEventType);
}

function getAuditAction(entry: WorkflowAuditEntryResponse): string | null {
  if (entry.event_type !== 'workflow_action') return null;
  const directAction = getString(entry.data.action);
  if (directAction) return directAction;
  const stepName = getString(entry.data.step_name ?? entry.data.stepName);
  return stepName;
}

export function getWorkflowAuditAction(entry: WorkflowAuditEntryResponse): string | null {
  return getAuditAction(entry);
}

function getAuditSearchText(entry: WorkflowAuditEntryResponse): string {
  const data = entry.data;
  const governance = getWorkflowAuditGovernanceContext(entry);
  const parts = [
    entry.event_type,
    entry.workflow_name,
    entry.conversation_id,
    entry.codebase_name,
    entry.codebase_id,
    entry.actor,
    entry.reason,
    getAuditAction(entry),
    governance.operator,
    governance.operatorRole,
    governance.createdBy,
    governance.updatedBy,
    governance.templateId,
    governance.templateName,
    governance.templateOwner,
    governance.templateRolloutState,
    governance.overrideState,
    governance.violationType,
    getString(data.comment),
    getString(data.reason),
    getString(data.workflow_name),
    getString(data.workflowName),
    getString(data.status_before),
    getString(data.statusBefore),
    getString(data.approval_node_id),
  ];
  return parts
    .filter((part): part is string => Boolean(part))
    .join(' ')
    .toLowerCase();
}

function getCreatedAtMillis(entry: WorkflowAuditEntryResponse): number {
  const value = new Date(entry.created_at).getTime();
  return Number.isNaN(value) ? 0 : value;
}

function getTrendMillis(value: string | number): number {
  const timestamp = typeof value === 'number' ? value : new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function formatTrendBucketLabel(startMs: number, endMs: number): string {
  const durationMs = Math.max(endMs - startMs, 0);
  const durationHours = durationMs / (60 * 60 * 1000);
  const start = new Date(startMs);
  if (durationHours >= 24) {
    return start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  return start.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function matchesWorkflowAuditEventType(
  entry: WorkflowAuditEntryResponse,
  eventType?: WorkflowAuditEventFilter
): boolean {
  if (!eventType || eventType === 'all') return true;
  if (entry.event_type === eventType) return true;
  if (eventType === 'delete_tombstone' && entry.event_type === 'workflow_deleted') return true;
  if (entry.event_type === 'workflow_action') {
    return getAuditAction(entry) === eventType;
  }
  return false;
}

export function getWorkflowAuditDisplayMeta(entry: WorkflowAuditEntryResponse): {
  label: string;
  tone: WorkflowAuditTone;
} {
  switch (entry.event_type) {
    case 'admission_denied':
      return { label: 'Admission denied', tone: 'destructive' };
    case 'cancel':
      return { label: 'Cancelled', tone: 'warning' };
    case 'abandon':
      return { label: 'Abandoned', tone: 'warning' };
    case 'approve':
      return { label: 'Approved', tone: 'neutral' };
    case 'reject':
      return { label: 'Rejected', tone: 'destructive' };
    case 'template_rollout_denied':
      return { label: 'Template rollout denied', tone: 'warning' };
    case 'template_override_denied':
      return { label: 'Template override denied', tone: 'destructive' };
    case 'delete_tombstone':
    case 'workflow_deleted':
      return { label: 'Workflow deleted', tone: 'warning' };
    case 'workflow_action': {
      const action = getAuditAction(entry);
      switch (action) {
        case 'cancel':
          return { label: 'Cancelled', tone: 'warning' };
        case 'abandon':
          return { label: 'Abandoned', tone: 'warning' };
        case 'approve':
          return { label: 'Approved', tone: 'neutral' };
        case 'reject':
          return { label: 'Rejected', tone: 'destructive' };
        case 'delete':
          return { label: 'Deleted', tone: 'warning' };
        default:
          return { label: 'Workflow action', tone: 'neutral' };
      }
    }
    default: {
      const eventType = entry.event_type as string;
      return {
        label: eventType.replace(/_/g, ' '),
        tone: 'neutral',
      };
    }
  }
}

export function formatWorkflowAuditReason(entry: WorkflowAuditEntryResponse): string | null {
  if (entry.event_type !== 'workflow_action') {
    if (isTemplateGovernanceEventType(entry.event_type)) {
      const governance = getWorkflowAuditGovernanceContext(entry);
      const parts: string[] = [];
      const prettyReason = prettifyAuditReason(entry.reason);
      if (prettyReason) parts.push(prettyReason);
      if (governance.templateName) parts.push(`template: ${governance.templateName}`);
      if (governance.templateOwner) parts.push(`owner: ${governance.templateOwner}`);
      if (governance.templateRolloutState) {
        parts.push(`rollout: ${governance.templateRolloutState}`);
      }
      if (governance.overrideState) parts.push(`override: ${governance.overrideState}`);
      if (governance.operator) parts.push(`operator: ${governance.operator}`);
      if (governance.operatorRole) parts.push(`role: ${governance.operatorRole}`);
      if (governance.violationType) parts.push(`violation: ${governance.violationType}`);
      const reason = getString(entry.data.reason);
      if (reason) parts.push(`reason: ${reason}`);
      if (parts.length > 0) return parts.join(' · ');
    }

    if (
      entry.event_type === 'approve' ||
      entry.event_type === 'reject' ||
      entry.event_type === 'cancel' ||
      entry.event_type === 'abandon'
    ) {
      const parts: string[] = [];
      const previousStatus = getString(entry.data.previous_status);
      const reason = getString(entry.data.reason);
      const comment = getString(entry.data.comment);
      const approvalNodeId = getString(entry.data.approval_node_id);

      const prettyReason = prettifyAuditReason(entry.reason);
      if (prettyReason) parts.push(prettyReason);
      if (previousStatus) parts.push(`from ${previousStatus}`);
      if (approvalNodeId) parts.push(`at ${approvalNodeId}`);
      if (reason) parts.push(`reason: ${reason}`);
      if (comment) parts.push(`comment: ${comment}`);
      if (parts.length > 0) return parts.join(' · ');
    }

    const prettyReason = prettifyAuditReason(entry.reason);
    if (prettyReason) return prettyReason;
    const reason = getString(entry.reason) ?? getString(entry.data.reason);
    return reason ?? null;
  }

  const action = getAuditAction(entry);
  const data = entry.data;
  const parts: string[] = [];
  const workflowName = getString(data.workflow_name ?? data.workflowName);
  const statusBefore = getString(data.status_before ?? data.statusBefore);
  const approvalNodeId = getString(data.approval_node_id);
  const reason = getString(data.reason);
  const comment = getString(data.comment);

  if (workflowName) parts.push(workflowName);
  if (statusBefore) parts.push(`from ${statusBefore}`);
  if (approvalNodeId) parts.push(`at ${approvalNodeId}`);
  if (action) parts.push(`action: ${action}`);
  if (reason) parts.push(`reason: ${reason}`);
  if (comment) parts.push(`comment: ${comment}`);

  if (parts.length > 0) return parts.join(' · ');
  if (entry.reason.trim()) return entry.reason.trim();
  return reason ?? null;
}

export function workflowAuditEntryKey(entry: WorkflowAuditEntryResponse): string {
  const action = entry.event_type === 'workflow_action' ? getAuditAction(entry) : null;
  const governance = getWorkflowAuditGovernanceContext(entry);
  const normalizedData = {
    action,
    reason: getString(entry.data.reason),
    comment: getString(entry.data.comment),
    workflow_name: getString(entry.data.workflow_name ?? entry.data.workflowName),
    status_before: getString(entry.data.status_before ?? entry.data.statusBefore),
    approval_node_id: getString(entry.data.approval_node_id),
    templateId: governance.templateId,
    templateName: governance.templateName,
    templateOwner: governance.templateOwner,
    templateRolloutState: governance.templateRolloutState,
    overrideState: governance.overrideState,
    operator: governance.operator,
    violationType: governance.violationType,
  };

  return [
    entry.event_type,
    entry.workflow_run_id ?? '',
    entry.workflow_name ?? '',
    entry.codebase_id ?? '',
    entry.codebase_name ?? '',
    normalizedData.action ?? '',
    normalizedData.reason ?? '',
    normalizedData.comment ?? '',
    normalizedData.workflow_name ?? '',
    normalizedData.status_before ?? '',
    normalizedData.approval_node_id ?? '',
    normalizedData.templateId ?? '',
    normalizedData.templateName ?? '',
    normalizedData.templateOwner ?? '',
    normalizedData.templateRolloutState ?? '',
    normalizedData.overrideState ?? '',
    normalizedData.operator ?? '',
    normalizedData.violationType ?? '',
  ].join('|');
}

export function matchesWorkflowAuditFilters(
  entry: WorkflowAuditEntryResponse,
  filters: WorkflowAuditFilters
): boolean {
  if (filters.codebaseId && entry.codebase_id !== filters.codebaseId) return false;
  if (
    filters.codebaseNamePrefix?.trim() &&
    !(entry.codebase_name ?? '')
      .toLowerCase()
      .startsWith(filters.codebaseNamePrefix.trim().toLowerCase())
  ) {
    return false;
  }
  if (
    filters.workflowName?.trim() &&
    (entry.workflow_name ?? '').toLowerCase() !== filters.workflowName.trim().toLowerCase()
  ) {
    return false;
  }
  if (
    filters.conversationId?.trim() &&
    (entry.conversation_id ?? '').toLowerCase() !== filters.conversationId.trim().toLowerCase()
  ) {
    return false;
  }
  const actorFilter = filters.actor?.trim().toLowerCase();
  if (
    actorFilter &&
    ![
      entry.actor,
      getWorkflowAuditGovernanceContext(entry).operator,
      getWorkflowAuditGovernanceContext(entry).createdBy,
      getWorkflowAuditGovernanceContext(entry).updatedBy,
    ]
      .filter((value): value is string => Boolean(value))
      .some(value => value.toLowerCase().includes(actorFilter))
  ) {
    return false;
  }
  if (!matchesWorkflowAuditEventType(entry, filters.eventType)) return false;

  const createdAt = getCreatedAtMillis(entry);
  if (filters.after) {
    const after = new Date(filters.after).getTime();
    if (!Number.isNaN(after) && createdAt < after) return false;
  }
  if (filters.before) {
    const before = new Date(filters.before).getTime();
    if (!Number.isNaN(before) && createdAt >= before) return false;
  }

  const search = filters.search?.trim().toLowerCase();
  if (!search) return true;
  return getAuditSearchText(entry).includes(search);
}

function escapeCsvValue(value: string): string {
  if (!/[",\n\r]/.test(value)) return value;
  return `"${value.replaceAll('"', '""')}"`;
}

export function exportWorkflowAuditEntriesCsv(entries: WorkflowAuditEntryResponse[]): string {
  const header = [
    'created_at',
    'event_type',
    'label',
    'workflow_name',
    'conversation_id',
    'codebase_id',
    'codebase_name',
    'workflow_run_id',
    'reason',
    'details',
  ];

  const rows = entries.map(entry => {
    const { label } = getWorkflowAuditDisplayMeta(entry);
    const details = formatWorkflowAuditReason(entry) ?? '';
    const values = [
      entry.created_at,
      entry.event_type,
      label,
      entry.workflow_name ?? '',
      entry.conversation_id ?? '',
      entry.codebase_id ?? '',
      entry.codebase_name ?? '',
      entry.workflow_run_id ?? '',
      entry.reason,
      details,
    ];
    return values.map(value => escapeCsvValue(value)).join(',');
  });

  return [header.join(','), ...rows].join('\n');
}

export function summarizeWorkflowAuditEntries(
  entries: WorkflowAuditEntryResponse[]
): WorkflowAuditSummary {
  const summary: WorkflowAuditSummary = {
    total: entries.length,
    admissionDenied: 0,
    workflowActions: 0,
    cancelled: 0,
    abandoned: 0,
    approved: 0,
    rejected: 0,
    deleted: 0,
  };

  for (const entry of entries) {
    if (entry.event_type === 'admission_denied') summary.admissionDenied += 1;
    if (
      entry.event_type === 'workflow_action' ||
      entry.event_type === 'cancel' ||
      entry.event_type === 'abandon' ||
      entry.event_type === 'approve' ||
      entry.event_type === 'reject'
    ) {
      summary.workflowActions += 1;
      const action =
        entry.event_type === 'workflow_action' ? getWorkflowAuditAction(entry) : entry.event_type;
      if (action === 'cancel') summary.cancelled += 1;
      if (action === 'abandon') summary.abandoned += 1;
      if (action === 'approve') summary.approved += 1;
      if (action === 'reject') summary.rejected += 1;
      if (action === 'delete') summary.deleted += 1;
    }
    if (entry.event_type === 'delete_tombstone' || entry.event_type === 'workflow_deleted') {
      summary.deleted += 1;
    }
  }

  return summary;
}

export function summarizeWorkflowAuditGovernance(
  entries: WorkflowAuditEntryResponse[]
): WorkflowAuditGovernanceSummary {
  const summary: WorkflowAuditGovernanceSummary = {
    total: entries.length,
    violations: 0,
    overrideBlocked: 0,
    rolloutBlocked: 0,
    ownershipViolations: 0,
    draftTemplates: 0,
    activeTemplates: 0,
    archivedTemplates: 0,
    lockedOverrides: 0,
    allowedOverrides: 0,
    topOwner: null,
    topOwnerCount: 0,
    topOperator: null,
    topOperatorCount: 0,
  };

  const ownerCounts = new Map<string, number>();
  const operatorCounts = new Map<string, number>();

  for (const entry of entries) {
    if (!isTemplateGovernanceEventType(entry.event_type)) continue;

    summary.violations += 1;
    const governance = getWorkflowAuditGovernanceContext(entry);
    if (entry.event_type === 'template_override_denied') summary.overrideBlocked += 1;
    if (entry.event_type === 'template_rollout_denied') summary.rolloutBlocked += 1;

    switch (governance.templateRolloutState) {
      case 'draft':
        summary.draftTemplates += 1;
        break;
      case 'active':
        summary.activeTemplates += 1;
        break;
      case 'archived':
        summary.archivedTemplates += 1;
        break;
    }

    if (governance.allowCodebaseOverride === false || governance.overrideState === 'locked') {
      summary.lockedOverrides += 1;
    }
    if (governance.allowCodebaseOverride === true || governance.overrideState === 'allowed') {
      summary.allowedOverrides += 1;
    }

    if (governance.templateOwner) {
      const nextCount = (ownerCounts.get(governance.templateOwner) ?? 0) + 1;
      ownerCounts.set(governance.templateOwner, nextCount);
      if (nextCount > summary.topOwnerCount) {
        summary.topOwner = governance.templateOwner;
        summary.topOwnerCount = nextCount;
      }
    }

    if (governance.operator) {
      const nextCount = (operatorCounts.get(governance.operator) ?? 0) + 1;
      operatorCounts.set(governance.operator, nextCount);
      if (nextCount > summary.topOperatorCount) {
        summary.topOperator = governance.operator;
        summary.topOperatorCount = nextCount;
      }
    }
  }

  return summary;
}

export function summarizeWorkflowAuditPolicySources(
  entries: WorkflowAuditEntryResponse[],
  codebases: {
    id: string;
    workflow_admission_policy: Record<string, unknown> | null;
  }[]
): WorkflowAuditPolicySourceSummary {
  const codebaseSources = new Map<string, string>();
  for (const codebase of codebases) {
    const source = getString(codebase.workflow_admission_policy?.source) ?? 'unknown';
    codebaseSources.set(codebase.id, source);
  }

  const summary: WorkflowAuditPolicySourceSummary = {
    total: entries.length,
    sources: {},
    topSource: null,
    topSourceCount: 0,
  };

  for (const entry of entries) {
    const source = entry.codebase_id
      ? (codebaseSources.get(entry.codebase_id) ?? 'unknown')
      : 'unknown';
    summary.sources[source] = (summary.sources[source] ?? 0) + 1;
    if (summary.sources[source] > summary.topSourceCount) {
      summary.topSource = source;
      summary.topSourceCount = summary.sources[source];
    }
  }

  return summary;
}

export function buildWorkflowTrendBuckets(
  samples: WorkflowTrendSample[],
  options: WorkflowTrendBucketOptions = {}
): WorkflowTrendBucket[] {
  const points = samples
    .map(sample => ({
      timestamp: getTrendMillis(sample.timestamp),
      numerator: Number.isFinite(sample.numerator) ? Math.max(0, sample.numerator) : 0,
      denominator: Number.isFinite(sample.denominator) ? Math.max(0, sample.denominator) : 0,
    }))
    .filter(sample => sample.timestamp > 0);

  if (points.length === 0) return [];

  const bucketCount = Math.max(1, Math.trunc(options.bucketCount ?? 8));
  const optionStart = options.after ? getTrendMillis(options.after) : 0;
  const optionEnd = options.before ? getTrendMillis(options.before) : 0;
  const inferredStart = Math.min(...points.map(point => point.timestamp));
  const inferredEnd = Math.max(...points.map(point => point.timestamp)) + 1;
  const windowStart = optionStart > 0 ? optionStart : inferredStart;
  const windowEnd = optionEnd > windowStart ? optionEnd : inferredEnd;

  if (windowEnd <= windowStart) {
    const numerator = points.reduce((sum, point) => sum + point.numerator, 0);
    const denominator = points.reduce((sum, point) => sum + point.denominator, 0);
    return [
      {
        label: formatTrendBucketLabel(windowStart, windowEnd),
        start: new Date(windowStart).toISOString(),
        end: new Date(windowEnd).toISOString(),
        numerator,
        denominator,
        ratio: denominator > 0 ? numerator / denominator : 0,
      },
    ];
  }

  const bucketWidth = Math.max((windowEnd - windowStart) / bucketCount, 1);
  const buckets = Array.from({ length: bucketCount }, (_, index) => {
    const start = windowStart + index * bucketWidth;
    const end = index === bucketCount - 1 ? windowEnd : windowStart + (index + 1) * bucketWidth;
    return {
      start,
      end,
      numerator: 0,
      denominator: 0,
    };
  });

  for (const point of points) {
    const rawIndex = Math.floor((point.timestamp - windowStart) / bucketWidth);
    const index = Math.min(bucketCount - 1, Math.max(0, rawIndex));
    const bucket = buckets[index];
    if (!bucket) continue;
    bucket.numerator += point.numerator;
    bucket.denominator += point.denominator;
  }

  return buckets.map(bucket => ({
    label: formatTrendBucketLabel(bucket.start, bucket.end),
    start: new Date(bucket.start).toISOString(),
    end: new Date(bucket.end).toISOString(),
    numerator: bucket.numerator,
    denominator: bucket.denominator,
    ratio: bucket.denominator > 0 ? bucket.numerator / bucket.denominator : 0,
  }));
}

export function downloadTextFile(filename: string, content: string, mimeType = 'text/plain'): void {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function mergeWorkflowAuditEntries(
  persistedEntries: WorkflowAuditEntryResponse[],
  liveEntries: WorkflowAuditEntryResponse[]
): WorkflowAuditEntryResponse[] {
  const merged = new Map<string, WorkflowAuditEntryResponse>();
  for (const entry of persistedEntries) {
    merged.set(workflowAuditEntryKey(entry), entry);
  }
  for (const entry of liveEntries) {
    const key = workflowAuditEntryKey(entry);
    if (!merged.has(key)) {
      merged.set(key, entry);
    }
  }

  return [...merged.values()].sort(
    (a, b) => getCreatedAtMillis(b) - getCreatedAtMillis(a) || a.id.localeCompare(b.id)
  );
}

export function normalizeWorkflowActionEvent(
  event: WorkflowActionSSEEvent
): WorkflowAuditEntryResponse | null {
  const eventRecord = event as unknown as Record<string, unknown>;
  const data = event.data ?? {};
  const action =
    getString(data.action) ??
    getString(event.action) ??
    getString(event.step_name ?? event.stepName);
  if (!action) return null;

  const workflowName =
    getString(event.workflow_name) ??
    getString(event.workflowName) ??
    getString(data.workflow_name) ??
    getString(data.workflowName);
  const codebaseId =
    getString(event.codebase_id) ?? getString(event.codebaseId) ?? getString(data.codebase_id);
  const codebaseName =
    getString(event.codebase_name) ??
    getString(event.codebaseName) ??
    getString(data.codebase_name) ??
    getString(data.codebaseName);
  const workflowRunId =
    getString(event.workflow_run_id) ??
    getString(event.runId) ??
    getString(data.workflow_run_id) ??
    getString(data.runId);
  const conversationId =
    getString(eventRecord.conversation_id) ??
    getString(eventRecord.conversationId) ??
    getString(data.conversation_id) ??
    getString(data.conversationId);

  const normalizedData = {
    ...data,
    action,
    workflow_name: workflowName,
    codebase_id: codebaseId,
    codebase_name: codebaseName,
    workflow_run_id: workflowRunId,
    reason: getString(event.reason) ?? getString(data.reason) ?? '',
    comment: getString(event.comment) ?? getString(data.comment) ?? '',
  };

  const summary =
    formatWorkflowAuditReason({
      id: event.id ?? '',
      workflow_run_id: workflowRunId,
      workflow_name: workflowName,
      conversation_id: conversationId,
      codebase_id: codebaseId,
      codebase_name: codebaseName,
      event_type: 'workflow_action',
      reason: '',
      created_at: new Date(event.timestamp).toISOString(),
      data: normalizedData,
    }) ?? action;

  const baseEntry: WorkflowAuditEntryResponse = {
    id:
      event.id ??
      `live-${event.timestamp}-${workflowRunId ?? 'no-run'}-${action}-${
        codebaseId ?? 'no-codebase'
      }`,
    workflow_run_id: workflowRunId,
    workflow_name: workflowName,
    conversation_id: conversationId,
    codebase_id: codebaseId,
    codebase_name: codebaseName,
    event_type: 'workflow_action',
    reason: summary,
    created_at: new Date(event.timestamp).toISOString(),
    data: normalizedData,
  };

  return baseEntry;
}
